import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useCurrentUser } from '@ingenico/react-section-utils';
import type { DeploymentTargetDeviceResourceById } from '@ingenico/react-ui';
import { CoreStatus } from '@ingenico/react-ui/core-status';
import { ListViewItem, useRenderDefaultColumHeader } from '@ingenico/react-ui/datagrid';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './useDeploymentVirtualDevicesTabColumns.module.scss';

export function useDeploymentVirtualDevicesTabColumns(): {
  defaultAvailableColumns: string[];
  columns: GridColDef[];
} {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.DEPLOYMENT, ALL_LNGS_NAMESPACES.DEPLOYMENTS]);
  const currentUser = useCurrentUser();
  const { getUserDateTimeFormatWithTimezone } = useUserDateTimeFormat(currentUser?.dateTimeFormat);

  const renderCellValue = useCallback(
    (params: GridRenderCellParams<DeploymentTargetDeviceResourceById>): ReactNode =>
      params?.value ? (
        <ListViewItem primaryText={t(params.value, { ns: ALL_LNGS_NAMESPACES.DEPLOYMENTS })} />
      ) : (
        t('notAvailable')
      ),
    [t],
  );

  const renderSerialAndPartNumber = useCallback(
    (
      params: GridRenderCellParams<DeploymentTargetDeviceResourceById, { serialNumber?: string; partNumber?: string }>,
    ): ReactNode =>
      params?.value?.serialNumber || params?.value?.partNumber ? (
        <ListViewItem primaryText={params?.value?.serialNumber} secondaryText={params?.value?.partNumber} />
      ) : (
        t('notAvailable')
      ),
    [t],
  );

  const renderCellDate = useCallback(
    (params: GridRenderCellParams<DeploymentTargetDeviceResourceById>) =>
      params.value ? (
        <ListViewItem
          primaryText={getUserDateTimeFormatWithTimezone({
            instant: params.value,
            timezone: currentUser?.zoneInfo,
          })}
        />
      ) : (
        t('notAvailable')
      ),
    [currentUser?.zoneInfo, getUserDateTimeFormatWithTimezone, t],
  );

  const renderStatus = useCallback(
    (params: GridRenderCellParams<DeploymentTargetDeviceResourceById>): ReactNode => {
      const deploymentUnit = params.row;
      return deploymentUnit.computedStatusLabel ? (
        <CoreStatus
          transparentBackground
          label={t(deploymentUnit.computedStatusLabel, { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT })}
          status={deploymentUnit.computedStatusColor}
          customColorsClassNames={{ label: styles.labelTextColor }}
        />
      ) : (
        t('notAvailable')
      );
    },
    [t],
  );

  const { defaultRenderColumnHeader } = useRenderDefaultColumHeader(t);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        renderHeader: defaultRenderColumnHeader('field.name', ALL_LNGS_NAMESPACES.DEPLOYMENTS),
        headerName: t('field.name', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENTS }),
        filterable: false,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: renderCellValue,
      },
      {
        field: 'description',
        renderHeader: defaultRenderColumnHeader('field.description', ALL_LNGS_NAMESPACES.DEPLOYMENTS),
        headerName: t('field.description', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENTS }),
        filterable: false,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: renderCellValue,
      },
      {
        field: 'serialAndPartNumber',
        renderHeader: defaultRenderColumnHeader('field.serialAndPartNumber', ALL_LNGS_NAMESPACES.DEPLOYMENTS),
        headerName: t('field.serialAndPartNumber', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENTS }),
        filterable: false,
        flex: 1.3,
        minWidth: 150,
        sortable: false,
        renderCell: renderSerialAndPartNumber,
        valueGetter: (_, row: DeploymentTargetDeviceResourceById) => ({
          serialNumber: row.physicalDevice?.serialNumber,
          partNumber: row.physicalDevice?.partNumber,
        }),
      },
      {
        field: 'computedType',
        renderHeader: defaultRenderColumnHeader('field.type', ALL_LNGS_NAMESPACES.DEPLOYMENTS),
        headerName: t('field.type', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENTS }),
        filterable: false,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellValue,
      },
      {
        field: 'configurationProfile',
        renderHeader: defaultRenderColumnHeader('field.configurationProfile', ALL_LNGS_NAMESPACES.DEPLOYMENTS),
        headerName: t('field.configurationProfile', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENTS }),
        filterable: false,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: renderCellValue,
        valueGetter: (_, row: DeploymentTargetDeviceResourceById) => row.computedConfigurationProfile,
      },
      {
        field: 'creationDate',
        renderHeader: defaultRenderColumnHeader('field.creationDate', ALL_LNGS_NAMESPACES.DEPLOYMENTS),
        headerName: t('field.creationDate', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENTS }),
        flex: 1.3,
        minWidth: 150,
        filterable: false,
        renderCell: renderCellDate,
      },
      {
        field: 'startDate',
        renderHeader: defaultRenderColumnHeader('field.targetDevices.startDate', ALL_LNGS_NAMESPACES.DEPLOYMENT),
        headerName: t('field.targetDevices.startDate', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        filterable: false,
        flex: 1.3,
        minWidth: 150,
        renderCell: renderCellDate,
      },
      {
        field: 'endDate',
        renderHeader: defaultRenderColumnHeader('field.targetDevices.endDate', ALL_LNGS_NAMESPACES.DEPLOYMENT),
        headerName: t('field.targetDevices.endDate', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENT }),
        filterable: false,
        flex: 1.3,
        minWidth: 150,
        renderCell: renderCellDate,
      },
      {
        field: 'status',
        renderHeader: defaultRenderColumnHeader('field.status', ALL_LNGS_NAMESPACES.DEPLOYMENTS),
        headerName: t('field.status', { ns: ALL_LNGS_NAMESPACES.DEPLOYMENTS }),
        filterable: false,
        flex: 1,
        minWidth: 150,
        renderCell: renderStatus,
      },
    ],
    [defaultRenderColumnHeader, t, renderCellValue, renderSerialAndPartNumber, renderCellDate, renderStatus],
  );

  const defaultAvailableColumns = columns.map((column) => column.field);

  return {
    columns,
    defaultAvailableColumns,
  };
}
